import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const FlagMX = () => (
  <svg viewBox="0 0 640 480" className="w-5 h-4 rounded-sm">
    <path fill="#006847" d="M0 0h640v480H0z"/><path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
    <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/><circle cx="320" cy="240" r="60" fill="#006847"/>
  </svg>
);
const FlagUS = () => (
  <svg viewBox="0 0 640 480" className="w-5 h-4 rounded-sm">
    <path fill="#bd3d44" d="M0 0h640v480H0"/><path d="M0 55.3h640M0 129h640M0 203h640M0 277h640M0 351h640M0 425h640" stroke="#fff" strokeWidth="37"/>
    <path fill="#192f5d" d="M0 0h364.8v258.5H0"/>
  </svg>
);
const FlagBR = () => (
  <svg viewBox="0 0 640 480" className="w-5 h-4 rounded-sm">
    <path fill="#229e45" d="M0 0h640v480H0z"/><path fill="#f8e509" d="M320 76.8L584.7 240 320 403.2 55.3 240z"/>
    <circle cx="320" cy="240" r="76.8" fill="#2b49a3"/>
  </svg>
);

const languages = [
  { code:'es', name:'Español', flag:FlagMX },
  { code:'en', name:'English', flag:FlagUS },
  { code:'pt', name:'Português', flag:FlagBR }
];

export const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [isScrolled,setIsScrolled] = useState(false);
  const [mobileMenuOpen,setMobileMenuOpen] = useState(false);
  const [langMenuOpen,setLangMenuOpen] = useState(false);
  const [userMenuOpen,setUserMenuOpen] = useState(false);

  useEffect(()=>{
    const onScroll=()=>setIsScrolled(window.scrollY>40);
    window.addEventListener('scroll',onScroll);
    return()=>window.removeEventListener('scroll',onScroll);
  },[]);
  useEffect(()=>setMobileMenuOpen(false),[location.pathname]);

  const lang = (i18n.language || 'es').split('-')[0];
  const labels = {
    es:{home:'Inicio',technology:'Technology',art:'Creative Art',projects:'Proyectos',about:'Nosotros',contact:'Contacto'},
    en:{home:'Home',technology:'Technology',art:'Creative Art',projects:'Projects',about:'About',contact:'Contact'},
    pt:{home:'Início',technology:'Technology',art:'Creative Art',projects:'Projetos',about:'Nós',contact:'Contato'}
  }[lang] || null;

  const navLinks = [
    {path:'/',label:labels.home},
    {path:'/technology',label:labels.technology},
    {path:'/creative-art',label:labels.art},
    {path:'/portfolio',label:labels.projects},
    {path:'/about',label:labels.about},
    {path:'/contact',label:labels.contact}
  ];

  const changeLanguage = code => {
    i18n.changeLanguage(code);
    localStorage.setItem('gs-agency-lang',code);
    setLangMenuOpen(false);
  };
  const currentLanguage = languages.find(x=>x.code===lang) || languages[0];
  const CurrentFlag = currentLanguage.flag;

  const getDashboardPath=()=>{
    if(!user) return '/login';
    switch(user.role){
      case 'super_admin': case 'admin': return '/admin';
      case 'curator': return '/curator';
      case 'artist': return '/artist';
      case 'model': return '/model';
      case 'venue_owner': return '/venue';
      case 'transport_provider': return '/transport-provider';
      default:return '/login';
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled?'bg-black/85 backdrop-blur-xl border-b border-white/8 py-2':'bg-transparent py-4'}`}>
      <div className="container-premium flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center shrink-0" aria-label="GS Agency">
          <img src="/brand-assets/gs-agency-logo-silver.png" alt="GS Agency Art & Technology" className="h-12 md:h-14 w-auto object-contain"/>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {navLinks.map(link=>(
            <Link key={link.path} to={link.path}
              className={`gs-nav-link ${location.pathname===link.path?'active':''}`}>
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <div className="relative">
            <button onClick={()=>setLangMenuOpen(v=>!v)} className="flex items-center gap-2 text-white/55 hover:text-white px-3 py-2">
              <CurrentFlag/><span className="text-xs uppercase tracking-wider">{lang}</span><ChevronDown size={13}/>
            </button>
            <AnimatePresence>
              {langMenuOpen && (
                <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:8}}
                  className="absolute right-0 top-full mt-2 bg-[#080808]/98 border border-white/10 min-w-[160px] shadow-2xl">
                  {languages.map(x=>(
                    <button key={x.code} onClick={()=>changeLanguage(x.code)}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-white/5 ${lang===x.code?'text-white':'text-white/55'}`}>
                      <x.flag/><span>{x.name}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {isAuthenticated ? (
            <div className="relative">
              <button onClick={()=>setUserMenuOpen(v=>!v)} className="flex items-center gap-2 text-white/75">
                {user?.picture?<img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover"/>:<div className="w-8 h-8 border border-white/15 rounded-full flex items-center justify-center"><User size={15}/></div>}
                <ChevronDown size={13}/>
              </button>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:8}}
                    className="absolute right-0 top-full mt-2 bg-[#080808] border border-white/10 min-w-[190px]">
                    <div className="px-4 py-3 border-b border-white/8">
                      <p className="text-sm text-white truncate">{user?.name}</p>
                      <p className="text-xs text-white/38 truncate">{user?.email}</p>
                    </div>
                    <Link to={getDashboardPath()} className="block px-4 py-3 text-sm text-white/65 hover:bg-white/5" onClick={()=>setUserMenuOpen(false)}>{t('nav.dashboard')}</Link>
                    <button onClick={()=>{logout();setUserMenuOpen(false)}} className="w-full px-4 py-3 text-left text-sm text-white/55 hover:bg-white/5">{t('nav.logout')}</button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ):(
            <Link to="/login" className="btn-metal-outline !py-2.5 !px-5 text-sm">{t('nav.login')}</Link>
          )}
        </div>

        <button onClick={()=>setMobileMenuOpen(v=>!v)} className="xl:hidden text-white p-2">
          {mobileMenuOpen?<X size={24}/>:<Menu size={24}/>}
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}} exit={{opacity:0,height:0}}
            className="xl:hidden bg-black/96 backdrop-blur-xl border-t border-white/8">
            <nav className="container-premium py-6 flex flex-col">
              {navLinks.map(link=><Link key={link.path} to={link.path} className="py-3 border-b border-white/6 text-white/70">{link.label}</Link>)}
              <div className="flex gap-2 py-5">
                {languages.map(x=><button key={x.code} onClick={()=>changeLanguage(x.code)} className={`px-3 py-2 border text-sm flex items-center gap-2 ${lang===x.code?'border-white/35 text-white':'border-white/10 text-white/45'}`}><x.flag/>{x.code.toUpperCase()}</button>)}
              </div>
              {isAuthenticated?(
                <>
                  <Link to={getDashboardPath()} className="py-3 text-white/70">{t('nav.dashboard')}</Link>
                  <button onClick={logout} className="text-left py-3 text-white/50">{t('nav.logout')}</button>
                </>
              ):<Link to="/login" className="btn-metal text-center mt-3">{t('nav.login')}</Link>}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
